import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { useShortcutDialogOs } from "@/hooks/use-shortcut-os"
import {
  KEYBOARD_SHORTCUTS_CATALOG,
  keysForOs,
  type ShortcutCatalogItem,
} from "@/lib/keyboard-shortcuts-catalog"
import { detectShortcutOs } from "@/lib/keyboard-platform"
import { cn } from "@/lib/utils"

interface KeyboardShortcutsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

function ShortcutKeys({ keys }: { keys: string[] }) {
  return (
    <div className="flex items-center gap-1 flex-shrink-0">
      {keys.map((key, keyIdx) => (
        <kbd
          key={`${key}-${keyIdx}`}
          className="px-2 py-1 bg-muted border border-border rounded text-xs font-mono min-w-[24px] text-center"
        >
          {key}
        </kbd>
      ))}
    </div>
  )
}

function ShortcutRow({ item, os }: { item: ShortcutCatalogItem; os: 'mac' | 'pc' }) {
  const keys = keysForOs(item, os)
  return (
    <div className="py-1.5">
      <div className="flex items-center justify-between gap-4">
        <span className="text-sm text-muted-foreground">{item.description}</span>
        <ShortcutKeys keys={keys} />
      </div>
      {item.note ? (
        <p className="text-xs text-muted-foreground/80 mt-1 pr-2">{item.note}</p>
      ) : null}
      {!item.wired ? (
        <p className="text-xs text-amber-600/90 mt-1">Not wired in code yet.</p>
      ) : null}
    </div>
  )
}

export function KeyboardShortcutsDialog({ open, onOpenChange }: KeyboardShortcutsDialogProps) {
  const { displayOs, detected, setDisplayOs, resetDisplayOs, isOverridden } =
    useShortcutDialogOs()

  const detectedLabel = detected === 'mac' ? 'macOS' : 'Windows / Linux'

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="fixed top-4 right-4 left-auto flex max-h-[min(85vh,720px)] w-full max-w-2xl translate-x-0 translate-y-0 flex-col gap-0 overflow-hidden p-0 data-[state=closed]:slide-out-to-top-2 data-[state=closed]:slide-out-to-right-2 data-[state=open]:slide-in-from-top-2 data-[state=open]:slide-in-from-right-2 sm:max-w-2xl"
        data-testid="keyboard-shortcuts-dialog"
      >
        <DialogHeader className="flex-shrink-0 gap-3 border-b border-border px-6 py-4">
          <DialogTitle>Keyboard Shortcuts</DialogTitle>
          <div className="space-y-2 text-xs">
            <p className="text-muted-foreground">
              Shortcuts on your device use <span className="font-medium text-foreground">{detectedLabel}</span>.
              Toggle below to preview labels for another platform.
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <div
                className="inline-flex rounded-md border border-border p-0.5"
                role="group"
                aria-label="Shortcut platform preview"
              >
                {(['mac', 'pc'] as const).map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => setDisplayOs(option)}
                    className={cn(
                      'px-3 py-1 rounded-sm text-xs font-medium transition-colors',
                      displayOs === option
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    )}
                    data-testid={`keyboard-shortcuts-os-${option}`}
                  >
                    {option === 'mac' ? 'macOS' : 'Windows / Linux'}
                  </button>
                ))}
              </div>
              {isOverridden ? (
                <button
                  type="button"
                  onClick={resetDisplayOs}
                  className="text-muted-foreground underline-offset-2 hover:underline"
                >
                  Reset preview to {detectedLabel}
                </button>
              ) : (
                <span className="text-muted-foreground">
                  Preview: {displayOs === 'mac' ? 'macOS' : 'Windows / Linux'}
                </span>
              )}
            </div>
          </div>
        </DialogHeader>

        <div
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 py-4"
          data-testid="keyboard-shortcuts-scroll"
        >
          <div className="space-y-6">
            {KEYBOARD_SHORTCUTS_CATALOG.map((category) => (
              <div key={category.category}>
                <h3 className="font-semibold mb-3 text-sm text-foreground">{category.category}</h3>
                <div className="space-y-1">
                  {category.items.map((item) => (
                    <ShortcutRow key={item.id} item={item} os={displayOs} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
